import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

type Tone = 'neutral' | 'success' | 'warning' | 'destructive' | 'brand';

export interface ToastProps {
  tone?: Tone;
  children: ReactNode;
  className?: string;
  /**
   * Где живёт тост:
   *   - 'inline' — внутри потока документа (под полем ввода, в модалке)
   *   - 'fixed'  — плавает над контентом (внизу экрана)
   */
  placement?: 'inline' | 'fixed';
  role?: 'status' | 'alert';
}

const tones: Record<Tone, string> = {
  neutral: 'bg-tg-section-bg text-tg-text',
  brand: 'bg-brand text-brand-on',
  success: 'bg-success-subtle text-success',
  warning: 'bg-warning-subtle text-warning',
  destructive: 'bg-destructive-subtle text-destructive',
};

export function Toast({
  tone = 'neutral',
  children,
  className,
  placement = 'inline',
  role,
}: ToastProps) {
  const computedRole = role ?? (tone === 'destructive' || tone === 'warning' ? 'alert' : 'status');
  return (
    <div
      role={computedRole}
      aria-live={computedRole === 'alert' ? 'assertive' : 'polite'}
      className={cn(
        'rounded-xl px-3 py-2 text-sm',
        tones[tone],
        placement === 'fixed' &&
          'fixed bottom-6 left-1/2 -translate-x-1/2 shadow-md z-50 max-w-[calc(100%-2rem)]',
        className,
      )}
    >
      {children}
    </div>
  );
}
